import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import CollapseHeader from "../../../../core/common/collapse-header";
import { AuthContext } from "../../../../context/authcontext";
import { useSalesUserId } from "../../../../utils/salesUserScope";
import { ExportDropdown, ExportKind } from "../../Dashboard/common";
import { downloadExportFile } from "../../Dashboard/dashboardApi";
import { exportDashboardPdf } from "../../Dashboard/exportDashboardPdf";
import "./chatAnalytics.css";
import {
  BotFilterOption,
  ChatAnalyticsDashboardResponse,
  FilterOption,
  exportChatAnalyticsExcel,
  fetchChatAnalyticsDashboard,
  fetchChatStatusOptions,
  fetchChatTypeOptions,
} from "./chatAnalyticsService";
import FilterChips, { FilterChipItem } from "../../../../components/filters/FilterChips";
import ChatAnalyticsFilters from "./ChatAnalyticsFilters";
import ChatAnalyticsScoreCards from "./ChatAnalyticsScoreCards";
import ChatChannelChart from "./ChatChannelChart";
import ChatChannelVolumeChart from "./ChatChannelVolumeChart";
import ChatScopeDonut from "./ChatScopeDonut";
import ChatSourceDonut from "./ChatSourceDonut";
import ChatAutomationGauge from "./ChatAutomationGauge";
import ConnectedBotsList from "./ConnectedBotsList";
import ChatAgentWorkloadChart from "./ChatAgentWorkloadChart";
import ChatTeamsSection from "./ChatTeamsSection";
import ChatConversationsTable from "./ChatConversationsTable";
import { getPlatformDisplayName } from "./chatAnalyticsPalette";

const toIsoOrNull = (date: Date | null) => (date ? date.toISOString() : null);

interface ChannelOption extends FilterOption {
  platformId: number | null;
}

const buildChannelOptions = (res: ChatAnalyticsDashboardResponse): ChannelOption[] =>
  res.chat_counts_by_channels
    .filter((c) => c.external_chat_channel_id !== null && c.external_chat_channel_id !== undefined)
    .map((c) => ({
      value: c.external_chat_channel_id as number,
      label: c.external_chat_channel,
      platformId: c.external_chat_platform_id ?? null,
    }));

const buildBotOptions = (res: ChatAnalyticsDashboardResponse): BotFilterOption[] =>
  res.connected_bot_list.map((bot) => ({
    value: bot.external_chat_tenant_bot_id || bot.bot_name,
    label: bot.bot_name,
    isInternal: bot.chat_type_key === "INTERNAL",
  }));

const buildPlatformOptions = (res: ChatAnalyticsDashboardResponse): FilterOption[] => {
  if (res.external_chat_platforms.length) {
    return res.external_chat_platforms
      .filter((platform) => platform.external_chat_platform_id !== null && platform.external_chat_platform_id !== undefined)
      .map((platform) => ({
        value: platform.external_chat_platform_id,
        label: getPlatformDisplayName(
          platform.external_chat_platform_display_name || platform.external_chat_platform_key || `Platform ${platform.external_chat_platform_id}`
        ),
      }));
  }
  const names = new Map<number, string>();
  res.connected_bot_list.forEach((bot) => {
    const id = bot.external_chat_platform_id;
    if (id !== null && id !== undefined && bot.bot_type && !names.has(id)) {
      names.set(id, getPlatformDisplayName(bot.bot_type));
    }
  });
  res.chat_counts_by_channels.forEach((c) => {
    const id = c.external_chat_platform_id;
    if (id === null || id === undefined || names.has(id)) return;
    if (c.external_chat_channel_id === null || c.external_chat_channel_id === undefined) {
      names.set(id, c.external_chat_channel);
    }
  });
  res.chat_counts_by_channels.forEach((c) => {
    const id = c.external_chat_platform_id;
    if (id !== null && id !== undefined && !names.has(id)) names.set(id, `Platform ${id}`);
  });
  return Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

const ChartLoadingSkeleton: React.FC<{
  variant: "bars" | "donut" | "grid";
  height: number;
}> = ({ variant, height }) => {
  if (variant === "donut") {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height }}>
        <div
          className="ca-skeleton"
          style={{ width: height * 0.62, height: height * 0.62, borderRadius: "50%" }}
        />
      </div>
    );
  }
  if (variant === "grid") {
    return (
      <div style={{ height }} className="d-flex flex-column gap-2 justify-content-center">
        {[0, 1, 2].map((row) => (
          <div key={row} className="d-flex gap-2">
            {[0, 1, 2, 3, 4].map((col) => (
              <div key={col} className="ca-skeleton" style={{ flex: 1, height: 34 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  const bars = [0.9, 0.55, 0.75, 0.35, 0.6];
  return (
    <div className="ca-chart-skeleton" style={{ height }}>
      {bars.map((h, i) => (
        <span key={i} className="ca-skeleton" style={{ height: `${h * 100}%` }} />
      ))}
    </div>
  );
};

const ChatAnalytics = () => {
  const { selectedTenantId } = useContext(AuthContext);
  const scopedSalesUserId = useSalesUserId();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [platformId, setPlatformId] = useState<number | string | null>(null);
  const [channelId, setChannelId] = useState<number | string | null>(null);
  const [salesUserId, setSalesUserId] = useState<number | string | null>(null);
  const [teamId, setTeamId] = useState<number | string | null>(null);
  const [botId, setBotId] = useState<number | string | null>(null);
  const [statusId, setStatusId] = useState<number | null>(null);
  const [typeId, setTypeId] = useState<number | null>(null);

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<FilterOption[]>([]);
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<ChatAnalyticsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [initialFilterOptions, setInitialFilterOptions] = useState<{
    platforms: FilterOption[];
    channels: ChannelOption[];
    bots: BotFilterOption[];
    agents: FilterOption[];
    teams: FilterOption[];
  } | null>(null);

  useEffect(() => {
    if (!selectedTenantId) return;
    Promise.all([
      fetchChatStatusOptions(Number(selectedTenantId)),
      fetchChatTypeOptions(),
    ])
      .then(([statuses, types]) => {
        setStatusOptions(statuses);
        setTypeOptions(types);
      })
      .catch(() => { });
  }, [selectedTenantId]);

  const queryFilters = useMemo(
    () => ({
      startDatetime: toIsoOrNull(dateRange[0]),
      endDatetime: toIsoOrNull(dateRange[1]),
      salesUserId: scopedSalesUserId ?? salesUserId,
      assignedTeamId: teamId,
      externalChatEndUserStatusId: statusId,
      chatTypeId: typeId,
      externalChatTenantBotId: botId,
      externalChatChannelId: channelId,
      externalChatPlatformId: platformId,
    }),
    [dateRange, scopedSalesUserId, salesUserId, teamId, statusId, typeId, botId, channelId, platformId]
  );

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setLoading(true);

    const activeFiltersCount = [
      queryFilters.startDatetime,
      queryFilters.endDatetime,
      queryFilters.externalChatChannelId,
      queryFilters.externalChatPlatformId,
      salesUserId,
      queryFilters.assignedTeamId,
      queryFilters.externalChatTenantBotId,
      queryFilters.externalChatEndUserStatusId,
      queryFilters.chatTypeId,
    ].filter(Boolean).length;

    fetchChatAnalyticsDashboard(selectedTenantId, queryFilters)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
        if (activeFiltersCount === 0 && !initialFilterOptions) {
          setInitialFilterOptions({
            platforms: buildPlatformOptions(res),
            channels: buildChannelOptions(res),
            bots: buildBotOptions(res),
            agents: res.chat_counts_by_agents.map(a => ({ value: a.user_id || a.agent_name, label: a.agent_name })),
            teams: res.chat_counts_by_assigned_teams.map(t => ({ value: t.team_id || t.assigned_team_name, label: t.assigned_team_name })),
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load chat analytics data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, queryFilters, salesUserId]);

  const platformOptions: FilterOption[] = initialFilterOptions?.platforms || (data ? buildPlatformOptions(data) : []);
  const allChannelOptions: ChannelOption[] = initialFilterOptions?.channels || (data ? buildChannelOptions(data) : []);
  const channelOptions: FilterOption[] =
    platformId === null
      ? allChannelOptions
      : allChannelOptions.filter((option) => String(option.platformId) === String(platformId));

  const handlePlatformChange = (id: number | string | null) => {
    setPlatformId(id);
    if (id === null || channelId === null) return;
    const channel = allChannelOptions.find((option) => String(option.value) === String(channelId));
    if (!channel || String(channel.platformId) !== String(id)) setChannelId(null);
  };
  const salesUserOptions: FilterOption[] = initialFilterOptions?.agents || data?.chat_counts_by_agents.map(a => ({ value: a.user_id || a.agent_name, label: a.agent_name })) || [];
  const teamOptions: FilterOption[] = initialFilterOptions?.teams || data?.chat_counts_by_assigned_teams.map(t => ({ value: t.team_id || t.assigned_team_name, label: t.assigned_team_name })) || [];
  const botOptions: BotFilterOption[] = initialFilterOptions?.bots || (data ? buildBotOptions(data) : []);
  const connectedBots = data?.connected_bot_list ?? [];
  const connectedBotsCount = data ? data.connected_bots : 0;

  const describeChatFilters = () => {
    const labelOf = (options: FilterOption[], value: number | string | null) =>
      value === null ? null : options.find((option) => String(option.value) === String(value))?.label ?? String(value);
    const parts: string[] = [];
    if (dateRange[0] && dateRange[1]) {
      parts.push(`Date Range: ${dayjs(dateRange[0]).format("DD MMM YYYY")} - ${dayjs(dateRange[1]).format("DD MMM YYYY")}`);
    }
    const entries: [string, string | null][] = [
      ["Platform", labelOf(platformOptions, platformId)],
      ["Channel", labelOf(allChannelOptions, channelId)],
      ["Agent", labelOf(salesUserOptions, salesUserId)],
      ["Team", labelOf(teamOptions, teamId)],
      ["Bot", labelOf(botOptions, botId)],
      ["Status", labelOf(statusOptions, statusId)],
      ["Chat Type", labelOf(typeOptions, typeId)],
    ];
    entries.forEach(([label, value]) => {
      if (value) parts.push(`${label}: ${value}`);
    });
    return parts.length ? parts.join(" · ") : "No filters applied";
  };

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || !data || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        await exportDashboardPdf({
          container,
          sectionSelector: ".ca-pdf-section",
          title: "Chat Analytics",
          filtersText: describeChatFilters(),
          fileName: `chat-analytics-${dayjs().format("YYYY-MM-DD")}.pdf`,
        });
      } else {
        const file = await exportChatAnalyticsExcel(selectedTenantId, queryFilters);
        await downloadExportFile(file, `chat-analytics-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setPlatformId(null);
    setChannelId(null);
    setSalesUserId(null);
    setTeamId(null);
    setBotId(null);
    setStatusId(null);
    setTypeId(null);
  };

  const filterChips: FilterChipItem[] = [];
  if (dateRange[0] && dateRange[1]) {
    filterChips.push({
      key: "date",
      icon: "ti ti-calendar",
      label: "Date",
      value: `${dayjs(dateRange[0]).format("DD MMM YYYY")} - ${dayjs(dateRange[1]).format("DD MMM YYYY")}`,
      onRemove: () => setDateRange([null, null]),
    });
  }
  if (platformId !== null) {
    const selectedPlatform = platformOptions.find((p) => String(p.value) === String(platformId));
    filterChips.push({
      key: "platform",
      icon: "ti ti-device-laptop",
      label: "Platform",
      value: selectedPlatform?.label ?? String(platformId),
      onRemove: () => handlePlatformChange(null),
    });
  }
  if (channelId !== null) {
    const selectedChannel = channelOptions.find((c) => String(c.value) === String(channelId));
    filterChips.push({
      key: "channel",
      icon: "ti ti-brand-hipchat",
      label: "Channel",
      value: selectedChannel?.label ?? String(channelId),
      onRemove: () => setChannelId(null),
    });
  }
  if (salesUserId !== null) {
    const selectedSalesUser = salesUserOptions.find((u) => String(u.value) === String(salesUserId));
    filterChips.push({
      key: "salesUser",
      icon: "ti ti-user",
      label: "Sales Agent",
      value: selectedSalesUser?.label ?? String(salesUserId),
      onRemove: () => setSalesUserId(null),
    });
  }
  if (teamId !== null) {
    const selectedTeam = teamOptions.find((t) => String(t.value) === String(teamId));
    filterChips.push({
      key: "team",
      icon: "ti ti-users",
      label: "Team",
      value: selectedTeam?.label ?? String(teamId),
      onRemove: () => setTeamId(null),
    });
  }
  if (botId !== null) {
    const selectedBot = botOptions.find((b) => String(b.value) === String(botId));
    filterChips.push({
      key: "bot",
      icon: "ti ti-robot",
      label: "Bot",
      value: selectedBot?.label ?? String(botId),
      onRemove: () => setBotId(null),
    });
  }
  if (statusId !== null) {
    const selectedStatus = statusOptions.find((s) => String(s.value) === String(statusId));
    filterChips.push({
      key: "status",
      icon: "ti ti-progress-check",
      label: "Status",
      value: selectedStatus?.label ?? String(statusId),
      onRemove: () => setStatusId(null),
    });
  }
  if (typeId !== null) {
    const selectedType = typeOptions.find((t) => String(t.value) === String(typeId));
    filterChips.push({
      key: "type",
      icon: "ti ti-category",
      label: "Chat Type",
      value: selectedType?.label ?? String(typeId),
      onRemove: () => setTypeId(null),
    });
  }

  const scoreCards = data?.score_cards || null;
  const channels = data?.chat_counts_by_channels || [];
  const agents = data?.chat_counts_by_agents || [];
  const teams = data?.chat_counts_by_assigned_teams || [];

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-building-community fs-1 text-muted mb-3" />
                  <h5>Select a Tenant</h5>
                  <p className="text-muted mb-0">
                    Please select a tenant to view the chat analytics.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper chat-analytics-page">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center row-gap-2">
                <div className="col">
                  <div className="d-flex align-items-center flex-wrap gap-3">
                    <h3 className="page-title d-flex align-items-center mb-0">
                      Chat Analytics
                    </h3>
                    <FilterChips
                      chips={filterChips}
                      onClearAll={handleClearFilters}
                      disabled={loading}
                      className="mb-0"
                    />
                  </div>
                </div>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex title-head justify-content-end align-items-center gap-2">
                    <ExportDropdown
                      disabled={!data || loading}
                      exporting={exporting}
                      spinClassName="ca-spin"
                      onExport={handleExport}
                    />
                    <div className="daterange-picker d-flex align-items-center justify-content-center">
                      <div className="head-icons mb-0">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <ChatAnalyticsFilters
          loading={loading}
          dateRange={dateRange}
          onDateRangeChange={(start, end) => setDateRange([start, end])}
          platformOptions={platformOptions}
          platformId={platformId}
          onPlatformChange={handlePlatformChange}
          channelOptions={channelOptions}
          channelId={channelId}
          onChannelChange={setChannelId}
          salesUserOptions={salesUserOptions}
          salesUserId={salesUserId}
          onSalesUserChange={setSalesUserId}
          teamOptions={teamOptions}
          teamId={teamId}
          onTeamChange={setTeamId}
          botOptions={botOptions}
          botId={botId}
          onBotChange={setBotId}
          statusOptions={statusOptions}
          statusId={statusId}
          onStatusChange={setStatusId}
          typeOptions={typeOptions}
          typeId={typeId}
          onTypeChange={setTypeId}
          onClear={handleClearFilters}
        />

        <div ref={reportRef}>
          <div className="ca-pdf-section">
            <ChatAnalyticsScoreCards data={scoreCards} loading={loading} />
          </div>

          <div className="row row-gap-3 ca-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-indigo">
                    <i className="ti ti-chart-pie" />
                  </span>
                  <h5 className="mb-0">Total Chats — External vs Internal</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="donut" height={300} />
                  ) : (
                    <ChatScopeDonut data={data} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-teal">
                    <i className="ti ti-chart-bar" />
                  </span>
                  <h5 className="mb-0">Chats by Channel</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="bars" height={300} />
                  ) : (
                    <ChatChannelVolumeChart channels={channels} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row row-gap-3 ca-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-violet">
                    <i className="ti ti-chart-bar" />
                  </span>
                  <h5 className="mb-0">Volume by Channel</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="bars" height={340} />
                  ) : (
                    <ChatChannelChart channels={channels} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-pink">
                    <i className="ti ti-chart-donut" />
                  </span>
                  <h5 className="mb-0">Who Sent the Messages</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="donut" height={320} />
                  ) : (
                    <ChatSourceDonut data={scoreCards} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row row-gap-3 ca-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-amber">
                    <i className="ti ti-users" />
                  </span>
                  <h5 className="mb-0">Agent Workload</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="bars" height={300} />
                  ) : (
                    <ChatAgentWorkloadChart agents={agents} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="row row-gap-3">
                <div className="col-12 d-flex">
                  <div className="card flex-fill mb-4 ca-chart-card">
                    <div className="card-header">
                      <span className="ca-chart-icon icon-teal">
                        <i className="ti ti-gauge" />
                      </span>
                      <h5 className="mb-0">Automation Rate</h5>
                    </div>
                    <div className="card-body">
                      {loading ? (
                        <ChartLoadingSkeleton variant="donut" height={260} />
                      ) : (
                        <ChatAutomationGauge data={scoreCards} />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12 d-flex">
                  <div className="card flex-fill mb-4 ca-chart-card">
                    <div className="card-header">
                      <span className="ca-chart-icon icon-indigo">
                        <i className="ti ti-robot" />
                      </span>
                      <h5 className="mb-0">Connected Bots</h5>
                      {!loading && (
                        <span className="badge bg-light text-dark border ms-auto">{connectedBotsCount}</span>
                      )}
                    </div>
                    <div className="card-body">
                      {loading ? (
                        <ChartLoadingSkeleton variant="grid" height={120} />
                      ) : (
                        <ConnectedBotsList bots={connectedBots} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row row-gap-3 ca-pdf-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-violet">
                    <i className="ti ti-users-group" />
                  </span>
                  <h5 className="mb-0">Assigned Teams</h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <ChartLoadingSkeleton variant="bars" height={280} />
                  ) : (
                    <ChatTeamsSection teams={teams} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6 d-flex">
              <div className="card flex-fill mb-4 ca-chart-card">
                <div className="card-header">
                  <span className="ca-chart-icon icon-pink">
                    <i className="ti ti-building-community" />
                  </span>
                  <h5 className="mb-0">Internal Chat</h5>
                </div>
                <div className="card-body">
                  <div className="ca-internal-stats">
                    <div className="ca-internal-stat">
                      <b className="num">{loading ? "—" : scoreCards?.total_chats?.internal ?? 0}</b>
                      <span>Total Internal Chats</span>
                    </div>
                    <div className="ca-internal-stat">
                      <b className="num">
                        {loading ? "—" : scoreCards?.total_connected_bots?.internal ?? 0}
                      </b>
                      <span>Connected Google Chat Bots</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="row row-gap-3 ca-pdf-section">
              <div className="col-12 d-flex">
                <div className="card flex-fill mb-4 ca-chart-card">
                  <div className="card-header">
                    <span className="ca-chart-icon icon-amber">
                      <i className="ti ti-table" />
                    </span>
                    <h5 className="mb-0">Chats &amp; Messages by Participant</h5>
                  </div>
                  <div className="card-body">
                    <ChartLoadingSkeleton variant="grid" height={160} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ca-pdf-section">
              <ChatConversationsTable data={data?.tabular_view || null} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatAnalytics;
